import { Button, LoaderWrapper } from '@fl/cmsch-fe-library';
import { maxBy } from 'lodash/fp';
import React, { FC, Fragment, memo, PropsWithChildren, useCallback, useLayoutEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { opt, filter, pipe } from 'ts-opt';
import { Content } from 'app/content';
import { NavigationRestriction, pathRestrictions } from 'app/navigation';
import * as routerActions from 'app/router';
import { useOurTranslation } from 'app/translations';
import { userRoutePaths } from 'app/user/routes/route-paths';
import { userHasAccessComputation } from 'app/user/utils/user-has-access';
import { userAction } from '../../model/action';
import { simpleUserSelector } from '../../model/selector';

/**
 * Finds the most specific route restriction that matches the current path.
 *
 * @param restrictions Route restrictions to search through.
 * @returns A lookup function that resolves the best matching restriction.
 *
 * @example
 * const restriction = getCurrentPathRestriction(pathRestrictions)('/profil-uzivatele');
 */
export const getCurrentPathRestriction = (restrictions: Array<NavigationRestriction>) => (currentPath: string): NavigationRestriction | undefined => pipe(restrictions, filter((x: NavigationRestriction) => Boolean(x.path) && currentPath.startsWith(x.path)), maxBy(x => x.path.length));
const UserPermissionGuardBase: FC<PropsWithChildren> = props => {
  const {
    children
  } = props;
  const dispatch = useDispatch();
  const pathName = useSelector(simpleUserSelector.currentPathname);
  const {
    search
  } = useLocation();
  const {
    t
  } = useOurTranslation('user');
  const isUserLoading = useSelector(simpleUserSelector.isUserLoading);
  const isUserInitialized = useSelector(simpleUserSelector.isUserInitialized);
  const currentUserName = useSelector(simpleUserSelector.currentUser).prop('username');
  const currentUserPermissions = useSelector(simpleUserSelector.currentUserPermissions);
  const currentPathRestriction = useMemo(() => getCurrentPathRestriction(pathRestrictions)(pathName), [pathName]);
  const isAuthenticationPath = pathName === userRoutePaths.login || pathName === userRoutePaths.logout;
  const isProtectedPath = !isAuthenticationPath && Boolean(currentPathRestriction?.onlyForLoggedIn || currentPathRestriction?.permissions);
  const isAnonymous = currentUserName.isEmpty;
  useLayoutEffect(() => {
    if (!isAnonymous) return;
    if (!isUserInitialized && !isUserLoading) {
      dispatch(userAction.initialize(isProtectedPath));
      return;
    }
    if (isUserInitialized && !isUserLoading && isProtectedPath) {
      dispatch(routerActions.push(userRoutePaths.login, {
        redirectTo: `${pathName}${search}`
      }));
    }
  }, [dispatch, isAnonymous, isProtectedPath, isUserInitialized, isUserLoading, pathName, search]);
  const goToLogIn = useCallback(() => {
    dispatch(userAction.logOut(true));
  }, [dispatch]);
  const userHasAccess = useMemo(() => userHasAccessComputation(opt(currentUserPermissions), currentPathRestriction), [currentPathRestriction, currentUserPermissions]);
  if (isUserLoading && !isUserInitialized) {
    return <Content browserTabTitle="eSkot" noFooter>
                <LoaderWrapper loading stretch size="large" text={`${t('loadingUser')}`} />
            </Content>;
  }

  // The backend session check decides whether to restore the user or redirect
  // to login. Never render the permission error for an anonymous user while
  // that decision and navigation are in progress.
  if (isAnonymous && isProtectedPath) return null;
  if (!userHasAccess) {
    return <Content browserTabTitle="eSkot" noFooter>
                <div className="d-flex flex-column justify-content-center align-items-center">
                    <h3 className="text-center">{t('noPermissionMessage')}</h3>
                    <Button visuals="primary" onClick={goToLogIn}>
                        {t('loginAsAnother')}
                    </Button>
                </div>
            </Content>;
  }
  return <Fragment>
            {children}
        </Fragment>;
};

/**
 * Guards the user module content against loading and permission states.
 *
 * @param props Protected user-module content.
 * @returns The guarded children or a loading/permission screen.
 *
 * @example
 * <UserPermissionGuard>
 *     <AppRoutes />
 * </UserPermissionGuard>
 */
export const UserPermissionGuard = memo(UserPermissionGuardBase);